import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  ArrowLeft, 
  Send, 
  ExternalLink, 
  Pin, 
  MessageSquare, 
  Clock, 
  Lightbulb, 
  FileText,
  Target
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import RecentConversations from '../components/focus/RecentConversations';
import { useAskSwirlStore } from '../stores/askSwirlStore';
import chatService from '../services/chatService';
import focusService from '../services/focusService';
import { documentService } from '../services/documentService';

// ==================
// DUMMY DATA - REMOVED, using real data now
// ==================

// Old dummy data removed - now loading from database via chatService.getFocus()

const oldFocusData = {
  '1': {
    id: '1',
    title: 'Japan Trip',
    mission: 'Plan an unforgettable 2-week journey through Tokyo, Kyoto, and Osaka',
    coverImage: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1600&q=80',
    lastVisited: '2 days ago',
    itemsCount: 24,
    insightsCount: 7,
    
    // Recent Conversations - What you asked
    conversations: [
      {
        id: 'c1',
        content: 'Best time to visit for cherry blossoms?',
        timestamp: '2 days ago'
      },
      {
        id: 'c2',
        content: 'Should I get the 7-day or 14-day JR Pass?',
        timestamp: '5 days ago'
      },
      {
        id: 'c3',
        content: 'Vegan options in traditional Japanese restaurants?',
        timestamp: '1 week ago'
      },
      {
        id: 'c4',
        content: 'Best neighborhoods to stay in Tokyo?',
        timestamp: '1 week ago'
      }
    ],
    
    // Swirl Insights - What AI learned
    insights: [
      {
        id: 'i1',
        content: 'You have a strong preference for authentic local experiences over tourist attractions'
      },
      {
        id: 'i2',
        content: 'Most of your content focuses on Tokyo and Kyoto - consider exploring Osaka more'
      },
      {
        id: 'i3',
        content: 'Add a 15-20% buffer to your travel budget for spontaneous experiences'
      }
    ],
    
    // Memory Lane - What happened
    memoryItems: [
      {
        id: 'm1',
        content: 'You saved 8 ramen restaurant guides',
        timestamp: '3 days ago'
      },
      {
        id: 'm2',
        content: 'You last asked about cherry blossoms',
        timestamp: '2 days ago'
      },
      {
        id: 'm3',
        content: 'You haven\'t looked at Osaka items in 6 days',
        timestamp: '6 days ago'
      }
    ],
    
    // Your Work - Organized by category
    workPlans: [
      {
        id: 'w1',
        type: 'plan',
        title: '14-Day Japan Itinerary',
        description: 'Complete day-by-day schedule covering Tokyo, Kyoto, Osaka, and Nara',
        progress: 65,
        lastEdited: 'Yesterday',
        isPriority: true
      },
      {
        id: 'w3',
        type: 'plan',
        title: 'Packing List for Spring',
        description: 'Weather-appropriate clothing and cultural essentials',
        progress: 20,
        lastEdited: '1 week ago',
        isPriority: false
      }
    ],
    
    workOutlines: [
      {
        id: 'w2',
        type: 'outline',
        title: 'Restaurant Reservation Timeline',
        description: 'When to book premium spots like Kikunoi and Sukiyabashi Jiro',
        progress: 30,
        lastEdited: '3 days ago'
      }
    ],
    
    workInsights: [
      {
        id: 'w4',
        type: 'insight',
        title: 'Key Patterns',
        description: 'Most content focuses on food experiences and early morning activities',
        isInsight: true
      }
    ],
    
    // Your Library - Saved content
    libraryItems: [
      {
        id: 'l1',
        type: 'image',
        title: 'Shibuya Crossing at Night',
        source: 'Unsplash',
        imageUrl: 'https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=600&q=80',
        summary: 'Iconic Tokyo intersection - visit at sunset'
      },
      {
        id: 'l2',
        type: 'link',
        title: 'Best Ramen in Shinjuku',
        source: 'Tokyo Cheapo',
        summary: 'Guide to 8 authentic ramen shops'
      },
      {
        id: 'l3',
        type: 'image',
        title: 'Fushimi Inari Shrine',
        source: 'Pinterest',
        imageUrl: 'https://images.unsplash.com/photo-1478436127897-769e1b3f0f36?w=600&q=80',
        summary: 'Thousands of red torii gates in Kyoto'
      },
      {
        id: 'l4',
        type: 'note',
        title: 'Accommodation Budget',
        source: 'My Notes',
        summary: 'Shibuya Airbnb $85/night, Kyoto Ryokan $120/night'
      },
      {
        id: 'l5',
        type: 'image',
        title: 'Cherry Blossom Season',
        source: 'Instagram',
        imageUrl: 'https://images.unsplash.com/photo-1522383225653-ed111181a951?w=600&q=80',
        summary: 'Peak bloom late March to early April'
      },
      {
        id: 'l6',
        type: 'image',
        title: 'Tsukiji Outer Market',
        source: 'Unsplash',
        imageUrl: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80',
        summary: 'Fresh sushi breakfast - arrive by 7am'
      },
      {
        id: 'l7',
        type: 'link',
        title: 'Tokyo Metro Navigation',
        source: 'Hyperdia',
        summary: 'Offline maps and Suica card guide'
      },
      {
        id: 'l8',
        type: 'image',
        title: 'Arashiyama Bamboo Grove',
        source: 'Google',
        imageUrl: 'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=600&q=80',
        summary: 'Magical bamboo forest in western Kyoto'
      }
    ]
  }
};

// ==================
// CARD COMPONENTS
// ==================

// Conversation Card - Light, bubble-like, compact
function ConversationCard({ item }) {
  return (
    <div className="p-3 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 cursor-pointer group">
      <div className="flex items-start gap-2 mb-1.5">
        <MessageSquare size={12} className="text-[#7A47F5] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-900 leading-snug group-hover:text-[#7A47F5] transition-colors">
          {item.content}
        </p>
      </div>
      <span className="text-xs text-gray-400 pl-5">{item.timestamp}</span>
    </div>
  );
}

// Insight Card - Larger, purple accent, editorial
function InsightCard({ item }) {
  return (
    <div className="p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 shadow-sm">
      <div className="flex items-start gap-2.5">
        <Lightbulb size={14} className="text-[#7A47F5] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-gray-800 leading-relaxed font-medium">
          {item.content}
        </p>
      </div>
    </div>
  );
}

// Memory Card - Soft background, low contrast, translucent
function MemoryCard({ item }) {
  return (
    <div className="p-3 bg-gray-50/80 rounded-lg border border-gray-100">
      <div className="flex items-start gap-2 mb-1">
        <Clock size={11} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-gray-600 leading-relaxed">
          {item.content}
        </p>
      </div>
      <span className="text-xs text-gray-400 pl-4">{item.timestamp}</span>
    </div>
  );
}

// Work Card - Structured, hierarchical, actionable
function WorkCard({ item, isPriority = false }) {
  const typeIcons = {
    plan: <Target size={isPriority ? 18 : 16} className="text-gray-700" />,
    outline: <FileText size={16} className="text-gray-700" />
  };

  if (item.isInsight) {
    return (
      <div className="p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-purple-100 shadow-sm">
        <div className="flex items-center gap-2.5 mb-3">
          <Lightbulb size={18} className="text-[#7A47F5]" />
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
            {item.title}
          </h4>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">
          {item.description}
        </p>
      </div>
    );
  }

  const cardClasses = isPriority
    ? "p-6 bg-white rounded-2xl border border-gray-300 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer group"
    : "p-5 bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-300 cursor-pointer group";

  const titleClasses = isPriority
    ? "text-xl font-semibold text-gray-900 mb-2 leading-tight group-hover:text-[#7A47F5] transition-colors"
    : "text-lg font-semibold text-gray-900 mb-2 leading-snug group-hover:text-[#7A47F5] transition-colors";

  return (
    <div className={cardClasses}>
      {/* Top: Type Icon & Label */}
      <div className="flex items-center gap-2 mb-4">
        {typeIcons[item.type]}
        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
          {item.type}
        </span>
      </div>
      
      {/* Title - Bold and prominent */}
      <h4 className={titleClasses}>
        {item.title}
      </h4>
      
      {/* Description - Muted secondary line */}
      <p className="text-sm text-gray-600 leading-relaxed mb-5">
        {item.description}
      </p>
      
      {/* Progress Bar & Meta */}
      {item.progress !== undefined && (
        <div className="space-y-2.5">
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#7A47F5] rounded-full transition-all duration-500"
              style={{ width: `${item.progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">{item.progress}% complete</span>
            <span className="text-xs text-gray-500">Edited {item.lastEdited}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Library Card - Visual, gallery-like
function LibraryCard({ item, onAskSwirl }) {
  const [isHovered, setIsHovered] = useState(false);

  if (item.type === 'image') {
    return (
      <div
        className="group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg bg-gray-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={item.imageUrl}
          alt={item.title}
          className="w-full h-64 object-cover"
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <p className="text-xs text-white/60 mb-1 uppercase tracking-wide">{item.source}</p>
            <h4 className="text-sm font-medium text-white mb-1.5 leading-snug">{item.title}</h4>
            <p className="text-xs text-white/90 leading-relaxed">{item.summary}</p>
          </div>
        </div>

        {isHovered && (
          <div className="absolute top-3 right-3 flex gap-2">
            <button className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-lg">
              <ExternalLink size={12} className="text-gray-700" />
            </button>
            <button className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white transition-colors shadow-lg">
              <Pin size={12} className="text-gray-700" />
            </button>
            <button
              onClick={() => onAskSwirl(item)}
              className="p-2 bg-gray-900/90 backdrop-blur-sm rounded-lg hover:bg-gray-900 transition-colors shadow-lg"
            >
              <MessageSquare size={12} className="text-white" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Text-based content
  return (
    <div
      className="group p-4 rounded-2xl border border-gray-200 bg-white cursor-pointer transition-all duration-300 hover:shadow-md hover:border-gray-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start justify-between mb-2.5">
        <span className="text-xs text-gray-500 uppercase tracking-wide">{item.source}</span>
        <div className="px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wide bg-blue-50 text-blue-700">
          {item.type}
        </div>
      </div>

      <h4 className="text-sm font-medium text-gray-900 mb-1.5 leading-snug group-hover:text-[#7A47F5] transition-colors">
        {item.title}
      </h4>
      <p className="text-xs text-gray-600 leading-relaxed">
        {item.summary}
      </p>

      {isHovered && (
        <div className="absolute top-3 right-3 flex gap-2">
          <button className="p-1.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
            <ExternalLink size={10} className="text-gray-700" />
          </button>
          <button className="p-1.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
            <Pin size={10} className="text-gray-700" />
          </button>
          <button
            onClick={() => onAskSwirl(item)}
            className="p-1.5 bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <MessageSquare size={10} className="text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

// ==================
// MAIN COMPONENT
// ==================

export default function FocusDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isExpanded, toggle } = useSidebar();
  const { setFocusContext, clearFocusContext } = useAskSwirlStore();
  const [swirlInput, setSwirlInput] = useState('');
  
  // Real Focus data state
  const [focus, setFocus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [plans, setPlans] = useState([]);
  
  // Library state (separate from focus for clean state management)
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState(null);
  const [membershipsByItemId, setMembershipsByItemId] = useState({});
  
  // Set Focus context when component mounts
  useEffect(() => {
    if (id) {
      setFocusContext(id);
      console.log('🎯 [FocusDetailPage] Set Focus context:', id);
      
      // TODO: Update last_active_at for this focus (needs RPC function)
      // focusService.updateLastActive(id).catch(err => 
      //   console.warn('[FocusDetailPage] Failed to update last_active_at:', err)
      // );
    }
    
    // Clear Focus context when component unmounts
    return () => {
      clearFocusContext();
      console.log('🎯 [FocusDetailPage] Cleared Focus context');
    };
  }, [id, setFocusContext, clearFocusContext]);
  
  // Load Focus data
  useEffect(() => {
    if (user && id) {
      loadFocus();
    }
  }, [user, id]);
  
  const loadFocus = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await chatService.getFocus(user.id, id);
      
      if (err) {
        throw new Error(err);
      }
      
      setFocus(data);

      // Load plans for this focus
      const { data: focusPlans, error: plansError } = await documentService.getPlansByFocus(user.id, id);
      if (!plansError && focusPlans) {
        setPlans(focusPlans);
      }

      // Load library items for this focus
      setLibraryLoading(true);
      const { data: items, error: libError } = await focusService.getFocusLibraryItems(id);
      if (libError) {
        console.error('Failed to load library items:', libError);
        setLibraryError(libError);
      } else {
        setLibraryItems(items || []);
        
        // Load memberships (which focuses each item belongs to)
        if (items && items.length > 0) {
          const { data: memberships, error: membershipsError } = await focusService.getFocusMembershipsForItems(items.map(i => i.id));
          if (!membershipsError && memberships) {
            setMembershipsByItemId(memberships);
          }
        }
      }
      setLibraryLoading(false);
      
    } catch (err) {
      console.error('Error loading focus:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Focus...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || !focus) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            {error ? 'Error loading Focus' : 'Focus not found'}
          </h2>
          {error && <p className="text-sm text-gray-600 mb-4">{error}</p>}
          <button
            onClick={() => navigate('/focus')}
            className="text-[#7A47F5] hover:underline text-sm"
          >
            Back to Focuses
          </button>
        </div>
      </div>
    );
  }

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!swirlInput.trim()) return;
    
    console.log('Ask Swirl about Focus:', swirlInput, 'Focus ID:', id);
    
    // Navigate to AI chat page with Focus context already set in store
    navigate(`/ai-chat?prompt=${encodeURIComponent(swirlInput)}`);
    setSwirlInput('');
  };

  const handleAskSwirlAbout = (item) => {
    console.log('Ask Swirl about:', item.title);
  };

  return (
    <div className="flex h-screen bg-white">
      <Sidebar isExpanded={isExpanded} onToggle={toggle} />

      {!isExpanded && (
        <button
          onClick={toggle}
          className="fixed left-16 top-8 w-6 h-6 bg-white/40 backdrop-blur-lg rounded-r-lg flex items-center justify-center text-gray-800 hover:bg-white/50 transition-all duration-500 ease-out z-[60] shadow-lg border border-white/30"
        >
          <ChevronRight size={16} />
        </button>
      )}

      <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        
        {/* Back Button */}
        <div className="px-16 pt-10 pb-6">
          <button
            onClick={() => navigate('/focus')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back to Focuses</span>
          </button>
        </div>

        {/* HERO SECTION */}
        <div className="px-16 pb-12">
          <div className="max-w-[1800px] mx-auto">
            <div className="relative rounded-3xl overflow-hidden h-[420px] shadow-lg">
              {/* Background Image or Gradient */}
              {focus.cover_image_url ? (
                <>
                  <img
                    src={focus.cover_image_url}
                    alt={focus.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* Soft Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/25" />
                </>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-blue-600" />
              )}
              
              {/* Content */}
              <div className="relative h-full flex items-end">
                <div className="w-full px-14 py-12 flex items-end justify-between gap-12">
                  
                  {/* Left: Title and Mission */}
                  <div className="flex-1 max-w-3xl">
                    <h1 className="text-6xl font-medium text-white mb-5 tracking-tight leading-tight">
                      {focus.title}
                    </h1>
                    {focus.mission && (
                      <p className="text-xl text-white/95 leading-relaxed">
                        {focus.mission}
                      </p>
                    )}
                    {!focus.mission && focus.description && (
                      <p className="text-xl text-white/95 leading-relaxed">
                        {focus.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Right: Stats & Swirl Input */}
                  <div className="flex flex-col gap-5 w-[380px] flex-shrink-0">
                    
                    {/* Stats Card */}
                    <div className="bg-white/15 backdrop-blur-2xl rounded-2xl p-6 border border-white/25 shadow-xl">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2.5 text-sm text-white/90">
                          <Clock size={16} />
                          <span>Created {new Date(focus.created_at).toLocaleDateString()}</span>
                        </div>
                        {focus.description && (
                          <p className="text-sm text-white/80 leading-relaxed">
                            {focus.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Swirl Input Card */}
                    <div className="bg-white rounded-2xl p-6 shadow-2xl border border-white/50">
                      <h3 className="text-xs font-semibold text-gray-700 mb-4 uppercase tracking-wider">
                        Ask Swirl
                      </h3>
                      <form onSubmit={handleSendMessage} className="flex gap-2.5">
                        <input
                          type="text"
                          value={swirlInput}
                          onChange={(e) => setSwirlInput(e.target.value)}
                          placeholder="What do you want to think about?"
                          className="flex-1 px-4 py-3 bg-gray-50 border-0 rounded-xl text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7A47F5] focus:bg-white transition-all"
                        />
                        <button
                          type="submit"
                          className="p-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors flex-shrink-0"
                        >
                          <Send size={18} />
                        </button>
                      </form>
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* THREE-COLUMN WORKSPACE */}
        <div className="px-16 pb-24">
          <div className="max-w-[1800px] mx-auto">
            <div className="flex gap-8">
              
              {/* COLUMN A: YOUR BRAIN (25%) */}
              <div className="w-[25%] space-y-8 flex-shrink-0">
                
                {/* Section 1: Recent Conversations - REAL DATA */}
                <div>
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">
                      Recent Conversations
                    </h3>
                    <p className="text-xs text-gray-500">
                      What you asked
                    </p>
                  </div>
                  {user && id ? (
                    <RecentConversations focusId={id} userId={user.id} limit={5} />
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                      <p className="text-sm text-gray-500">Loading...</p>
                    </div>
                  )}
                </div>
                
                {/* Section 2: Swirl Insights */}
                <div>
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">
                      Swirl Insights
                    </h3>
                    <p className="text-xs text-gray-500">
                      What AI learned
                    </p>
                  </div>
                  <div className="space-y-3">
                    {(focus.insights || []).map((item) => (
                      <InsightCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
                
                {/* Section 3: Memory Lane */}
                <div>
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Memory Lane
                    </h3>
                    <p className="text-xs text-gray-400">
                      Your breadcrumbs
                    </p>
                  </div>
                  <div className="space-y-2">
                    {(focus.memoryItems || []).map((item) => (
                      <MemoryCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
                
              </div>
              
              {/* COLUMN B: YOUR WORK (35%) */}
              <div className="w-[35%] flex-shrink-0">
                
                {/* Section Header */}
                <div className="mb-6">
                  <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1.5">
                    Your Work
                  </h2>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Active plans, drafts, and outlines
                  </p>
                </div>
                
                {/* PLANS Group */}
                <div className="mb-8">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                    Plans
                  </h3>
                  <div className="space-y-4">
                    {plans.length > 0 ? (
                      plans.map((plan) => (
                        <button
                          key={plan.id}
                          onClick={() => navigate(`/ai-chat?openDocumentId=${plan.id}`)}
                          className="w-full text-left p-4 rounded-xl bg-white border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all duration-200 group"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-semibold text-neutral-900 text-sm group-hover:text-purple-600 transition-colors">
                              {plan.title || 'Untitled Plan'}
                            </h4>
                            <FileText size={16} className="text-gray-400 group-hover:text-purple-500 transition-colors" />
                          </div>
                          <p className="text-xs text-neutral-500 mb-2 line-clamp-2">
                            {plan.summary || 'No summary available'}
                          </p>
                          <div className="flex items-center justify-between text-[10px] text-neutral-400">
                            <span>Updated {new Date(plan.updated_at).toLocaleDateString()}</span>
                            <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-center p-8 text-gray-400 text-sm">
                        <FileText size={24} className="mx-auto mb-2 opacity-30" />
                        <p>No plans yet</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* OUTLINES Group */}
                <div className="mb-8">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                    Outlines
                  </h3>
                  <div className="space-y-4">
                    {(focus.workOutlines || []).map((item) => (
                      <WorkCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
                
                {/* INSIGHT WORK Group */}
                <div className="mb-8">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">
                    Insight Work
                  </h3>
                  <div className="space-y-4">
                    {(focus.workInsights || []).map((item) => (
                      <WorkCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
                
                {/* Create A Plan CTA */}
                <button
                  onClick={() => navigate('/ai-chat')}
                  className="w-full p-4 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2 font-medium text-sm"
                >
                  <span className="text-lg">+</span>
                  Create A Plan
                </button>
                
              </div>
              
              {/* COLUMN C: YOUR LIBRARY (40%) */}
              <div className="w-[40%] space-y-4 flex-shrink-0">
                <div className="mb-8">
                  <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1.5">
                    Your Library
                  </h2>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Everything you've collected
                  </p>
                </div>
                
                <div className="space-y-4">
                  {libraryLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-500">Loading library...</p>
                    </div>
                  ) : libraryError ? (
                    <div className="text-center py-8 text-red-600">
                      <p className="text-sm">Error loading library items</p>
                    </div>
                  ) : libraryItems.length > 0 ? (
                    libraryItems.map((item) => {
                      const otherFocuses = (membershipsByItemId[item.id] || []).filter(m => m.focus_id !== id);
                      
                      return (
                        <div key={item.id} className="p-4 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
                          <h4 className="text-sm font-medium text-gray-900 mb-1">
                            {item.title || 'Untitled'}
                          </h4>
                          <p className="text-xs text-gray-500 mb-2">
                            {item.source_type} • Added {new Date(item.added_at).toLocaleDateString()}
                          </p>
                          
                          {/* Also in chips */}
                          {otherFocuses.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2 items-center">
                              <span className="text-xs text-gray-500">Also in:</span>
                              {otherFocuses.slice(0, 2).map(f => (
                                <button
                                  key={f.focus_id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/focus/${f.focus_id}`);
                                  }}
                                  className="text-xs px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                >
                                  {f.focus_title}
                                </button>
                              ))}
                              {otherFocuses.length > 2 && (
                                <span className="text-xs px-2 py-1 rounded-full bg-gray-100">
                                  +{otherFocuses.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center p-12 bg-gray-50 rounded-2xl border border-gray-200">
                      <FileText size={48} className="text-gray-300 mx-auto mb-4" />
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">No items yet</h3>
                      <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                        Save items from Swirlspace to collect them here
                      </p>
                      <button
                        onClick={() => navigate('/swirlspace')}
                        className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        Go to Swirlspace
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
            </div>
          </div>
        </div>

      </main>

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
